import i18n from '../../i18n/config';
import { useTranslation } from 'react-i18next';
// Botón flotante "Preguntar a IA" + Drawer lateral con el Asistente APS.
// Se monta una sola vez en el Layout → disponible en toda la web.
// Cualquier módulo puede abrirlo con: window.dispatchEvent(new CustomEvent('aps-open-assistant', { detail: { prompt, channelId } }))
import React, { useState, useEffect, useCallback } from 'react';
import { Fab, Drawer, Box, Button, Typography, IconButton, Tooltip, Avatar, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import { useNavigate } from 'react-router-dom';
import AssistantPanel from './AssistantPanel';

export const openAssistant = (prompt = '', channelId = null, pageContext = null) => {
  window.dispatchEvent(new CustomEvent('aps-open-assistant', { detail: { prompt, channelId, pageContext } }));
};

// Deriva un contexto de página básico desde la URL actual (para que la IA sepa
// qué estás viendo aunque el módulo no lo pase explícitamente).
function detectPageContext() {
  try {
    const path = window.location.pathname || '';
    const m = path.match(/\/alarm-tickets\/([0-9a-f-]{8,})/i);
    if (m) return { module: 'ticket', entity_type: 'ticket', entity_id: m[1] };
    const tk = path.match(/\/tasks\/([0-9a-f-]{8,})/i);
    if (tk) return { module: 'task', entity_type: 'task', entity_id: tk[1] };
    const params = new URLSearchParams(window.location.search);
    const veh = params.get('vehicle');
    if (veh) return { module: 'vehicle', entity_type: 'vehicle', entity_id: veh };
    if (path.includes('/alarm-tickets')) return { module: 'tickets' };
    if (path.includes('/reports')) return { module: 'reports' };
    if (path.includes('/tasks')) return { module: 'tasks' };
    if (path.includes('/vehicles') || path.includes('/schematics')) return { module: 'vehicles' };
    return { module: 'dashboard' };
  } catch (e) { return null; }
}

export default function AssistantLauncher() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [channelId, setChannelId] = useState(null);
  const [pageContext, setPageContext] = useState(null);
  const [panelKey, setPanelKey] = useState(0);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleOpen = useCallback((e) => {
    const detail = (e && e.detail) || {};
    setChannelId(detail.channelId || null);
    setPageContext(detail.pageContext || detectPageContext());
    // El panel solo se reinicia cuando llega un prompt nuevo. Antes se
    // remontaba en CADA apertura, así que cerrar y volver a abrir borraba la
    // conversación; con `keepMounted` en el Drawer, el historial ahora persiste.
    if (detail.prompt) {
      setPrompt(detail.prompt);
      setPanelKey((k) => k + 1);
    }
    setOpen(true);
  }, []);

  useEffect(() => {
    window.addEventListener('aps-open-assistant', handleOpen);
    return () => window.removeEventListener('aps-open-assistant', handleOpen);
  }, [handleOpen]);

  // Escape cierra en desktop. El Drawer ya lo hace, pero el panel captura
  // teclas para el input y conviene garantizarlo desde el contenedor.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => { if (event.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <>
      <Tooltip title={t('hardcoded.components_ai_assistantlauncher.preguntar_a_ia')} placement="left">
        <Fab color="primary" aria-label={t('hardcoded.components_ai_assistantlauncher.preguntar_a_ia')}
          onClick={() => { setChannelId(null); setPageContext(detectPageContext()); setOpen(true); }}
          sx={{
            position: 'fixed', right: 24, zIndex: 1150,
            // Se levanta sobre la barra inferior del navegador móvil (iOS/Android).
            bottom: 'calc(24px + env(safe-area-inset-bottom, 0px))',
            // Con el panel abierto en móvil ocupa toda la pantalla: el Fab
            // sobraría y solo taparía contenido.
            display: open && isMobile ? 'none' : 'inline-flex',
            background: 'linear-gradient(135deg, #d32f2f 0%, #b71c1c 100%)',
          }}>
          <AutoAwesomeIcon />
        </Fab>
      </Tooltip>

      <Drawer
        anchor={isMobile ? 'bottom' : 'right'}
        open={open}
        onClose={() => setOpen(false)}
        // Mantiene el panel montado al cerrar: es lo que conserva la conversación.
        ModalProps={{ keepMounted: true }}
        // CAUSA REAL de "no se puede cerrar en móvil": el Navbar está en
        // `theme.zIndex.drawer + 1` (1201) y este Drawer se quedaba en 1200, así
        // que la barra superior se pintaba ENCIMA de la cabecera del panel y
        // tapaba justo el botón Cerrar. Se sube a nivel modal + margen para
        // quedar también sobre el aviso de tickets (1300) y el drawer de tickets
        // (1310/1311), pero por debajo de snackbar (1400) para no ocultar avisos.
        sx={{ zIndex: (t2) => t2.zIndex.modal + 20 }}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 420 },
            // `100dvh` sigue a la barra del navegador móvil cuando aparece o se
            // oculta; con `100vh` el input inferior quedaba fuera de pantalla.
            height: isMobile ? '100dvh' : '100%',
            maxHeight: isMobile ? '100dvh' : '100%',
            display: 'flex',
            flexDirection: 'column',
            borderTopLeftRadius: isMobile ? 0 : undefined,
          },
        }}
      >
        <Box sx={{
          px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0,
          color: '#fff', background: 'linear-gradient(135deg, #1a1a1a 0%, #b71c1c 100%)',
          // El notch de iOS se comía la cabecera y dejaba la X fuera de alcance.
          pt: 'calc(12px + env(safe-area-inset-top, 0px))',
        }}>
          <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.15)', width: 32, height: 32 }}>
            <AutoAwesomeIcon sx={{ fontSize: 20 }} />
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={700} lineHeight={1.1} noWrap>
              {i18n.t('hardcoded.components_ai_assistantlauncher.asistente_aps')}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.85 }} noWrap component="div">
              {t('hardcoded.components_ai_assistantlauncher.ia_nativa_de_tu_plataforma')}
            </Typography>
          </Box>

          {!isMobile && (
            <Tooltip title={t('hardcoded.components_ai_assistantlauncher.abrir_en_pantalla_completa')}>
              <IconButton size="small" sx={{ color: '#fff' }}
                onClick={() => { setOpen(false); navigate('/chat/ai'); }}>
                <OpenInFullIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          {/* En móvil un icono suelto se pierde: se usa un botón con texto y
              área táctil suficiente, que es lo que faltaba para poder salir. */}
          {isMobile ? (
            <Button
              onClick={() => setOpen(false)}
              startIcon={<CloseIcon />}
              aria-label="Cerrar asistente"
              sx={{
                color: '#fff', flexShrink: 0, minHeight: 44, px: 1.5,
                border: '1px solid rgba(255,255,255,0.35)', borderRadius: 2,
              }}
            >
              Cerrar
            </Button>
          ) : (
            <IconButton size="small" sx={{ color: '#fff' }} aria-label="Cerrar asistente"
              onClick={() => setOpen(false)}>
              <CloseIcon />
            </IconButton>
          )}
        </Box>

        <Box sx={{
          flex: 1, minHeight: 0,
          pb: 'env(safe-area-inset-bottom, 0px)',
        }}>
          <AssistantPanel key={panelKey} initialPrompt={prompt} channelId={channelId} pageContext={pageContext} />
        </Box>
      </Drawer>
    </>
  );
}
